<script lang="ts">
  // PageContainerDetail — `#/containers/:id`.
  //
  // Visual reference: openspec/changes/ui-redesign-v2/Untitled.fig
  // node ID 0:447 (PageContainerDetail 1200×742 — 5 children:
  // header, 4 stat tiles, 2-col body, 2-col body, log viewer).
  //
  // Spec scenarios (R-ContainerDetail):
  //   - :id parsed from the route before any data fetch fires (the
  //     router already does this — we read it from the route prop).
  //   - Shell-exec input is `disabled` with adjacent "Coming soon"
  //     label (TDD pointer 13). The agent has no docker exec backend
  //     endpoint yet — per design.md ADR-7 + proposal.md Out-of-Scope
  //     list, this stub stays disabled until PR 4+ adds the route.
  //   - Unknown id → "Container not found" + zero enabled mutate btns.
  //   - Log search filters to lines from :id only.
  //
  // Action chips (restart / stop / start) open the shared ActionModal
  // so the typed-confirm gate is honoured for every mutate. PR 3 of
  // auth-via-telegram-otp removed the previous TOTP input on the
  // modal — the agent's mutate gate dropped TOTP in PR 1 and the
  // UI now matches.

  import { onDestroy, onMount } from "svelte";
  import { createWsClient, type WsClient, type SnapshotState } from "../lib/ws";
  import { push } from "../lib/router";
  import StateBadge from "./StateBadge.svelte";
  import LineChart from "./LineChart.svelte";
  import { getContainerLogs } from "../lib/api";
  import ActionModal from "./ActionModal.svelte";
  import { formatBytes, formatDuration } from "../lib/format";
  import {
    restartContainer,
    startContainer,
    stopContainer,
  } from "../lib/mutate";
  import type { ContainerRowData, MutateAction } from "../lib/bindings";

  export let route: { params: Record<string, string> };

  const isBrowser = typeof window !== "undefined";
  const client: WsClient | null = isBrowser ? createWsClient() : null;
  if (client) client.connect();

  function emptySnapshot(): SnapshotState {
    return {
      host: null,
      containers: [],
      logLines: [],
      connectionState: "disconnected",
      lastError: null,
      lastUpdateMs: null,
      hostHistory: [],
      ramHistory: [],
      cpuHistory: {},
    };
  }

  let snapshot: SnapshotState = client ? initialFrom(client) : emptySnapshot();
  let unsub: (() => void) | null = null;

  function initialFrom(c: WsClient): SnapshotState {
    let v: SnapshotState = emptySnapshot();
    const u = c.snapshot.subscribe((next) => {
      v = next;
    });
    u();
    return v;
  }

  if (client) {
    unsub = client.snapshot.subscribe((next) => {
      snapshot = next;
    });
  }

  onDestroy(() => unsub?.());

  // --- derivations -------------------------------------------------------

  $: id = route.params.id ?? "";
  $: container = (snapshot.containers ?? []).find(
    (c) => c.name === id,
  ) as ContainerRowData | undefined;
  $: notFound = container === undefined;
  $: containerState = container?.state ?? "loading";
  $: containerImage = container?.image ?? "—";
  $: containerUptime = container?.uptime_seconds ?? Number(container?.uptime_secs ?? 0);
  $: containerMem = container?.mem_used ?? 0;
  // Rolling CPU series for this container's chart.
  $: containerCpuSeries = (snapshot.cpuHistory?.[container?.id ?? ""] ?? []).map(
    (p) => p,
  );

  // Log lines: history from /api/logs/{id} (SQLite) + live tail from
  // the WS frames. History loads on mount and on manual refresh; live
  // lines append in real time.
  let logSearch = "";
  let historyLines: Array<{ containerId: string; ts: number; line: string }> = [];
  let logsLoadError: string | null = null;
  let logsLoading = false;

  async function loadLogHistory(): Promise<void> {
    if (!id) return;
    logsLoading = true;
    logsLoadError = null;
    try {
      const entries = await getContainerLogs(id, { minutes: 360 });
      historyLines = entries.map((e) => ({
        containerId: id,
        ts: Number(e.ts_ms),
        line: e.line,
      }));
    } catch (e) {
      // 401/404 etc — show inline, keep any live lines visible.
      logsLoadError = e instanceof Error ? e.message : String(e);
    } finally {
      logsLoading = false;
    }
  }

  $: id, void loadLogHistory();
  $: containerLogLines = [
    ...historyLines,
    ...(snapshot.logLines ?? []).filter((l) => l.containerId === id),
  ];
  $: filteredLogLines = applyLogFilter(containerLogLines, logSearch);

  function applyLogFilter(
    lines: readonly { containerId: string; ts: number; line: string }[],
    term: string,
  ) {
    const q = term.trim().toLowerCase();
    if (q === "") return [...lines];
    return lines.filter((l) => l.line.toLowerCase().includes(q));
  }

  // --- action handlers ---------------------------------------------------

  let modalOpen = false;
  let modalAction: MutateAction = "restart";
  let modalContainer = "";
  let toastError: string | null = null;

  function fireAction(action: MutateAction): void {
    if (!container) return;
    modalAction = action;
    modalContainer = container.name;
    modalOpen = true;
  }

  function back(): void {
    push("/containers");
  }

  async function onModalConfirm(
    e: CustomEvent<Record<string, never>>,
  ): Promise<void> {
    // PR 3 of auth-via-telegram-otp: ActionModal no longer carries
    // a TOTP payload — it just signals "user confirmed".
    if (!container) {
      modalOpen = false;
      return;
    }
    const name = container.name;
    try {
      if (modalAction === "restart") {
        await restartContainer({ container: name });
      } else if (modalAction === "stop") {
        await stopContainer({ container: name });
      } else if (modalAction === "start") {
        await startContainer({ container: name });
      }
      modalOpen = false;
    } catch (err) {
      toastError = err instanceof Error ? err.message : "mutate failed";
    }
  }

  function onModalCancel(): void {
    modalOpen = false;
  }

  onMount(() => {
    // PR 3 has no async data fetch; the snapshot already carries the
    // container. The onMount hook exists so PR 7 can wire a log
    // subscription or shell-exec polling without changing the
    // component shape.
    return () => {};
  });
</script>

<section class="page-detail" aria-labelledby="detail-heading">
  <header class="page-detail__head">
    <button
      type="button"
      class="page-detail__back"
      aria-label="Back to containers list"
      data-testid="detail-back"
      on:click={back}
    >←</button>
    <nav class="page-detail__breadcrumb" aria-label="Breadcrumb">
      <span>Containers</span>
      <span aria-hidden="true">/</span>
      <span class="page-detail__crumb-current">{id || "—"}</span>
    </nav>
  </header>

  {#if notFound}
    <div class="page-detail__empty" data-testid="detail-empty">
      <p class="page-detail__empty-title">Container not found</p>
      <p class="page-detail__empty-detail">
        No container named <code>{id}</code> in the current snapshot.
      </p>
    </div>
  {:else}
    <header class="page-detail__title-row">
      <h2 id="detail-heading" class="page-detail__title">{id}</h2>
      <StateBadge state={containerState} />
      <div class="page-detail__chips">
        <button
          type="button"
          class="action-chip"
          data-action="restart"
          aria-label="Restart this container"
          on:click={() => fireAction("restart")}
        >Restart</button>
        <button
          type="button"
          class="action-chip"
          data-action="stop"
          aria-label="Stop this container"
          on:click={() => fireAction("stop")}
        >Stop</button>
        <button
          type="button"
          class="action-chip"
          data-action="start"
          aria-label="Start this container"
          on:click={() => fireAction("start")}
        >Start</button>
      </div>
    </header>

    <div class="page-detail__stat-row" data-testid="stat-row">
      <article class="stat-tile" data-testid="stat-tile">
        <span class="stat-tile__label">IMAGE</span>
        <span class="stat-tile__value mono">{containerImage}</span>
      </article>
      <article class="stat-tile" data-testid="stat-tile">
        <span class="stat-tile__label">UPTIME</span>
        <span class="stat-tile__value mono">{formatDuration(containerUptime * 1000)}</span>
      </article>
      <article class="stat-tile" data-testid="stat-tile">
        <span class="stat-tile__label">STATE</span>
        <span class="stat-tile__value">{containerState}</span>
      </article>
      <article class="stat-tile" data-testid="stat-tile">
        <span class="stat-tile__label">CPU</span>
        <span class="stat-tile__value mono">{container?.cpu_pct ?? 0}%</span>
        <LineChart series={containerCpuSeries} unit="%" label={`CPU history for ${id}`} height={72} />
      </article>
    </div>

    <div class="page-detail__body">
      <section class="page-detail__col" aria-labelledby="inspect-heading">
        <h3 id="inspect-heading" class="page-detail__col-title">Inspect / Resources</h3>
        <dl class="page-detail__kv">
          <div class="page-detail__kv-row">
            <dt>Memory used</dt>
            <dd class="mono">{formatBytes(containerMem)}</dd>
          </div>
          <div class="page-detail__kv-row">
            <dt>CPU %</dt>
            <dd class="mono">{container?.cpu_pct ?? 0}%</dd>
          </div>
          <div class="page-detail__kv-row">
            <dt>Age</dt>
            <dd class="mono">{formatDuration(containerUptime * 1000)}</dd>
          </div>
          <div class="page-detail__kv-row">
            <dt>Container id</dt>
            <dd class="mono">{container?.id ?? "—"}</dd>
          </div>
        </dl>
      </section>

    </div>

    <div class="page-detail__body">
      <section class="page-detail__col" aria-labelledby="action-heading">
        <h3 id="action-heading" class="page-detail__col-title">Actions</h3>
        <div class="page-detail__chip-cluster">
          <button
            type="button"
            class="action-chip action-chip--accent"
            on:click={() => fireAction("restart")}
          >Restart</button>
          <button
            type="button"
            class="action-chip"
            on:click={() => fireAction("stop")}
          >Stop</button>
          <button
            type="button"
            class="action-chip"
            on:click={() => fireAction("start")}
          >Start</button>
        </div>
      </section>

      <section class="page-detail__col" aria-labelledby="logs-heading">
        <h3 id="logs-heading" class="page-detail__col-title">Logs</h3>
        <div class="page-detail__log-toolbar">
            <input
              type="search"
              class="search-input"
              data-testid="log-search"
              placeholder="Filter logs for this container"
              aria-label="Filter logs"
              bind:value={logSearch}
            />
            <button
              type="button"
              class="action-chip"
              data-testid="logs-refresh"
              disabled={logsLoading}
              on:click={() => void loadLogHistory()}
            >{logsLoading ? "Loading…" : "Reload history"}</button>
          </div>
          {#if logsLoadError}
            <p class="page-detail__logs-error" role="alert">{logsLoadError}</p>
          {/if}
        <div class="page-detail__log" data-testid="log-body">
          {#each filteredLogLines as l (l.ts)}
            <div class="page-detail__log-line">
              <span class="page-detail__log-ts mono">{l.ts}</span>
              <span class="page-detail__log-text mono">{l.line}</span>
            </div>
          {/each}
          {#if filteredLogLines.length === 0}
            <div class="page-detail__log-empty">No log lines for this container.</div>
          {/if}
        </div>
      </section>
    </div>

{/if}

  {#if toastError}
    <div class="page-detail__toast page-detail__toast--err" role="alert">
      {toastError}
    </div>
  {/if}

  <ActionModal
    open={modalOpen}
    action={modalAction}
    container={modalContainer}
    on:confirm={onModalConfirm}
    on:cancel={onModalCancel}
  />
</section>

<style>
  .page-detail {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  .page-detail__head {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .page-detail__back {
    width: 32px;
    height: 32px;
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--text-mid);
    cursor: pointer;
  }
  .page-detail__back:hover {
    background: var(--surface-3);
    color: var(--text-strong);
  }

  .page-detail__breadcrumb {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    color: var(--text-mid);
    font: var(--type-body-sm);
  }
  .page-detail__crumb-current {
    color: var(--text-strong);
    font-weight: 500;
  }

  .page-detail__title-row {
    display: flex;
    align-items: center;
    gap: var(--space-4);
  }

  .page-detail__title {
    font: var(--type-h1);
    color: var(--text-strong);
  }

  .page-detail__chips {
    margin-left: auto;
    display: inline-flex;
    gap: var(--space-2);
  }

  .action-chip {
    height: 32px;
    padding: 0 var(--space-4);
    border-radius: var(--radius-sm);
    background: var(--surface-2);
    border: 1px solid var(--border);
    color: var(--text-strong);
    font: var(--type-body-sm);
    font-weight: 500;
    cursor: pointer;
  }
  .action-chip:hover {
    background: var(--surface-3);
  }
  .action-chip--accent {
    color: var(--accent-cyan);
    background: var(--accent-bg);
    border-color: rgba(79, 70, 229, 0.40);
  }
  .action-chip--accent:hover {
    background: rgba(79, 70, 229, 0.12);
  }

  .page-detail__stat-row {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: var(--space-3);
  }

  .stat-tile {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-4);
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
  }

  .stat-tile__label {
    font: var(--type-label-caps);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-mid);
    font-size: 11px;
  }

  .stat-tile__value {
    font: var(--type-mono-md);
    color: var(--text-strong);
    word-break: break-all;
  }

  .page-detail__body {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--space-4);
  }

  .page-detail__col {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-4);
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
  }

  .page-detail__col-title {
    font: var(--type-h2);
    color: var(--text-strong);
  }

  .page-detail__col-empty {
    color: var(--text-mid);
    font: var(--type-body);
    margin: 0;
  }

  .page-detail__kv {
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .page-detail__kv-row {
    display: grid;
    grid-template-columns: 140px 1fr;
    gap: var(--space-3);
    align-items: baseline;
  }
  .page-detail__kv-row dt {
    color: var(--text-mid);
    font: var(--type-label);
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .page-detail__kv-row dd {
    margin: 0;
    color: var(--text-strong);
    font: var(--type-mono-md);
  }

  .page-detail__chip-cluster {
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .page-detail__log {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    max-height: 240px;
    overflow: auto;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    padding: var(--space-3);
  }

  .page-detail__log-line {
    display: grid;
    grid-template-columns: 64px 1fr;
    gap: var(--space-3);
    color: var(--text-strong);
    font: var(--type-mono-md);
  }

  .page-detail__log-ts {
    color: var(--text-mid);
  }

  .page-detail__log-empty {
    color: var(--text-mid);
    font: var(--type-body-sm);
  }

  .page-detail__exec-stub {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-4);
    background: var(--surface);
    border: 1px dashed var(--border);
    border-radius: var(--radius-sm);
  }

  .page-detail__exec-row {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .page-detail__exec-pill {
    display: inline-flex;
    align-items: center;
    height: 22px;
    padding: 0 var(--space-3);
    border-radius: 999px;
    background: var(--warn-bg);
    color: var(--warn-amber);
    border: 1px solid rgba(245, 158, 11, 0.40);
    font: var(--type-label);
    font-weight: 500;
  }

  .page-detail__exec-detail {
    margin: 0;
    color: var(--text-mid);
    font: var(--type-body-sm);
  }

  .page-detail__empty {
    padding: var(--space-6);
    background: var(--surface);
    border: 1px dashed var(--border);
    border-radius: var(--radius-sm);
    text-align: center;
  }
  .page-detail__empty-title {
    margin: 0;
    font: var(--type-h2);
    color: var(--text-strong);
  }
  .page-detail__empty-detail {
    margin: var(--space-2) 0 0;
    color: var(--text-mid);
    font: var(--type-body);
  }
</style>